'use client';

import { getDictionary, Locales } from '@/app/[lang]/dictonaries';
import bgImage from '@/public/dashboard-bg.png';
import { Checkbox } from '@/components/ui/checkbox';
import Button from '@/components/ui/Button';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/utils/supabase/client';
import { Check, X } from 'lucide-react';
import { Suspense } from 'react';

function ManageContent({ params }: { params: { lang: Locales } }) {
  const searchParams = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [firstName, setFirstName] = useState('');
  const [fields, setFields] = useState({
    subRenew: true,
    preDelivery: true,
    postDelivery: true,
    marketing: false
  });

  const [dict, setDict] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<'loading' | 'invalidToken' | 'loaded'>(
    'loading'
  );
  const [savingStatus, setSavingStatus] = useState<'success' | 'error' | null>(
    null
  );

  const supabase = createClient();

  useEffect(() => {
    const loadDictionary = async () => {
      const dictionary = await getDictionary(params.lang);
      setDict(dictionary);
    };

    const loadPreferences = async () => {
      const { data, error } = await supabase.functions.invoke(
        'manage-email-preferences',
        {
          body: {
            token,
            email
          }
        }
      );
      if (data) {
        console.log('Got preferences:', data);
        setFields({
          subRenew: data.preferences.sub_renew,
          preDelivery: data.preferences.pre_delivery,
          postDelivery: data.preferences.post_delivery,
          marketing: data.preferences.marketing_emails
        });
        setFirstName(data.firstName);
        setStatus('loaded');
      } else {
        setStatus('invalidToken');
      }
    };

    loadDictionary();

    // Log the token when it's available
    if (token) {
      console.log('Got token:', token);
      console.log('Got email:', email);
      loadPreferences();
    } else {
      setStatus('invalidToken');
    }
  }, [params.lang, token]);

  const savePreferences = async () => {
    setIsSaving(true);
    const { data, error } = await supabase.functions.invoke(
      'manage-email-preferences',
      {
        body: {
          token,
          email,
          update: {
            sub_renew: fields.subRenew,
            pre_delivery: fields.preDelivery,
            post_delivery: fields.postDelivery,
            marketing_emails: fields.marketing
          }
        }
      }
    );

    setIsSaving(false);
    setTimeout(() => setSavingStatus(null), 2000);
    if (error) {
      console.error('Error saving preferences:', error);
      setSavingStatus('error');
    } else {
      setSavingStatus('success');
    }
  };

  return (
    <div
      className="relative flex flex-col items-center justify-center bg-black h-full w-full min-h-[100vh] py-20 px-8 bg-cover bg-center bg-fixed"
      style={{ backgroundImage: `url(${bgImage.src})` }}
    >
      <div className="absolute top-0 left-0 w-full h-full backdrop-blur-md"></div>
      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center py-28 px-8">
        <div className="w-full md:w-[900px] md:py-8 py-6 px-6 md:px-12 bg-black rounded-3xl min-h-[400px] flex flex-col justify-between">
          {status === 'loading' && (
            <>
              <div>
                <div className="w-full h-[40px] rounded-full shimmer mb-4"></div>
                <div className="w-1/2 h-[40px] rounded-full shimmer mb-4"></div>
                <div className="w-1/2 h-[40px] rounded-full shimmer mb-4"></div>
                <div className="w-1/2 h-[40px] rounded-full shimmer mb-4"></div>
              </div>
              <div className="w-full flex justify-end">
                <div className="w-1/3 h-[40px] rounded-full shimmer"></div>
              </div>
            </>
          )}
          {status === 'invalidToken' && (
            <>
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  The URL token is invalid.
                </h1>
                <p className="text-lg text-white/50">
                  Please check the link you received.
                </p>
              </div>
              <p className="text-lg text-white/50">
                Need help updating your email preferences? Contact us at{' '}
                <a href="mailto:info@calori.fi">info@calori.fi</a>
              </p>
            </>
          )}
          {status === 'loaded' && (
            <>
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  Hey {firstName},
                </h1>
                <p className="text-lg text-white/50 mb-8">
                  Let's set which emails you want to receive from us:
                </p>
                <div>
                  <div className="flex items-top gap-2">
                    <Checkbox
                      id="termsConsent"
                      className="mt-[3px] border-white/50 data-[state=checked]:bg-[#213735] data-[state=checked]:border-[#213735] data-[state=checked]:text-white"
                      checked={fields.subRenew}
                      onCheckedChange={(value) =>
                        setFields({ ...fields, subRenew: !!value })
                      }
                    />
                    <span
                      className={`text-md text-white ${!fields.subRenew && 'opacity-50'}`}
                    >
                      Subscription renewal notifications
                    </span>
                  </div>
                  <div className="flex items-top gap-2 mt-2">
                    <Checkbox
                      id="termsConsent"
                      className="mt-[3px] border-white/50 data-[state=checked]:bg-[#213735] data-[state=checked]:border-[#213735] data-[state=checked]:text-white"
                      checked={fields.preDelivery}
                      onCheckedChange={(value) =>
                        setFields({ ...fields, preDelivery: !!value })
                      }
                    />
                    <span
                      className={`text-md text-white ${!fields.preDelivery && 'opacity-50'}`}
                    >
                      Notifications before meal deliveries
                    </span>
                  </div>
                  <div className="flex items-top gap-2 mt-2">
                    <Checkbox
                      id="termsConsent"
                      className="mt-[3px] border-white/50 data-[state=checked]:bg-[#213735] data-[state=checked]:border-[#213735] data-[state=checked]:text-white"
                      checked={fields.postDelivery}
                      onCheckedChange={(value) =>
                        setFields({ ...fields, postDelivery: !!value })
                      }
                    />
                    <span
                      className={`text-md text-white ${!fields.postDelivery && 'opacity-50'}`}
                    >
                      Notifications after meal deliveries
                    </span>
                  </div>
                  <div className="flex items-top gap-2 mt-2">
                    <Checkbox
                      id="termsConsent"
                      className="mt-[3px] border-white/50 data-[state=checked]:bg-[#213735] data-[state=checked]:border-[#213735] data-[state=checked]:text-white"
                      checked={fields.marketing}
                      onCheckedChange={(value) =>
                        setFields({ ...fields, marketing: !!value })
                      }
                    />
                    <span
                      className={`text-md text-white ${!fields.marketing && 'opacity-50'}`}
                    >
                      New meals, wellness tips, progress updates, and other
                      non-essential notifications
                    </span>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-12">
                {savingStatus === 'success' ? (
                  <p className="flex gap-1 mb-2 leading-tight">
                    <Check className="mt-[2px] w-4 h-4" />
                    Preferences saved successfully
                  </p>
                ) : savingStatus === 'error' ? (
                  <p className="flex gap-1 mb-2 leading-tight">
                    <X className="mt-[2px] w-4 h-4" />
                    There was an error saving your preferences
                  </p>
                ) : (
                  ''
                )}
                <div className="w-full flex justify-end">
                  <Button
                    variant="slim"
                    className="min-w-1/3 w-fit text-[#213735] bg-white hover:bg-gray-200 text-sm"
                    onClick={savePreferences}
                    disabled={isSaving}
                    loading={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Email Preferences'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ManagePage({ params }: { params: { lang: Locales } }) {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ManageContent params={params} />
    </Suspense>
  );
}
